import React, { JSX, useEffect, useMemo, useRef, useState } from 'react';
import { ImageField, Field, Image } from '@sitecore-jss/sitecore-jss-nextjs';
import Icons from 'components/atoms/Icons';
import axios from 'axios';

type VideoPlayerProps = {
  Thumbnail: ImageField;
  VideoUrl: Field<string>;
  allowFullScreen?: boolean;
};

const VideoPlayer = (props: VideoPlayerProps): JSX.Element => {
  const [showPoster, setShowPoster] = useState(true);
  const [duration, setDuration] = useState<string | null>(null);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const { Thumbnail, VideoUrl, allowFullScreen = false } = props;

  const videoUrl = VideoUrl?.value ?? '';

  const videoMeta = useMemo(() => {
    if (!videoUrl) {
      return {
        isFreshpaintVideo: false,
        isYouTubeVideo: false,
      };
    }

    try {
      const url = new URL(videoUrl);
      const hostname = url.hostname.toLowerCase();

      return {
        isFreshpaintVideo: hostname.includes('freshpaint-video.com'),
        isYouTubeVideo:
          hostname.includes('youtube.com') || hostname.includes('youtu.be'),
      };
    } catch {
      const lowerVideoUrl = videoUrl.toLowerCase();

      return {
        isFreshpaintVideo: lowerVideoUrl.includes('freshpaint-video.com'),
        isYouTubeVideo:
          lowerVideoUrl.includes('youtube.com') ||
          lowerVideoUrl.includes('youtu.be'),
      };
    }
  }, [videoUrl]);

  const videoId = useMemo(() => {
    if (!videoUrl || !videoMeta.isFreshpaintVideo) return '';

    try {
      const url = new URL(videoUrl);

      return url.pathname
        .replace(/^\/+|\/+$/g, '')
        .replace(/^videos\//, '');
    } catch {
      const cleanUrl = videoUrl.split('?')[0].split('#')[0];
      const pathAfterDomain = cleanUrl.split('com/')[1] ?? cleanUrl;

      return pathAfterDomain
        .replace(/^\/+|\/+$/g, '')
        .replace(/^videos\//, '');
    }
  }, [videoUrl, videoMeta.isFreshpaintVideo]);

  const iframeSrc = useMemo(() => {
    if (!videoUrl) return '';

    try {
      const url = new URL(videoUrl);

      url.searchParams.set('autoplay', '0');
      url.searchParams.set('controls', '1');
      url.searchParams.set('playsinline', '1');

      if (videoMeta.isFreshpaintVideo) {
        url.searchParams.set('muted', '0');
        url.searchParams.set('showplaytext', '1');
      }

      if (videoMeta.isYouTubeVideo) {
        url.searchParams.set('mute', '0');
        url.searchParams.set('rel', '0');
      }

      return url.toString();
    } catch {
      const separator = videoUrl.includes('?') ? '&' : '?';

      if (videoMeta.isYouTubeVideo) {
        return `${videoUrl}${separator}autoplay=0&controls=1&playsinline=1&mute=0&rel=0`;
      }

      return `${videoUrl}${separator}autoplay=0&muted=0&controls=1&playsinline=1&showplaytext=1`;
    }
  }, [videoUrl, videoMeta.isFreshpaintVideo, videoMeta.isYouTubeVideo]);

  const fallbackThumbnailUrl = useMemo(() => {
    if (!videoUrl || !videoMeta.isFreshpaintVideo) return '';

    try {
      const url = new URL(videoUrl);

      url.search = '';
      url.hash = '';

      return `${url.origin}${url.pathname.replace(/\/$/, '')}/thumbnail`;
    } catch {
      const cleanUrl = videoUrl.split('?')[0].split('#')[0];

      return `${cleanUrl.replace(/\/$/, '')}/thumbnail`;
    }
  }, [videoUrl, videoMeta.isFreshpaintVideo]);

  const hasAuthoredThumbnail = Boolean(Thumbnail?.value?.src);

  const shouldUseFreshpaintFallbackThumbnail =
    !hasAuthoredThumbnail &&
    videoMeta.isFreshpaintVideo &&
    Boolean(fallbackThumbnailUrl);

  const shouldRenderCustomPoster =
    showPoster && (hasAuthoredThumbnail || shouldUseFreshpaintFallbackThumbnail);

  const formatISOTime = (iso: string): string | undefined => {
    const match = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);

    if (!match) {
      console.log('Video duration format does not match expected format.');
      return undefined;
    }

    const hours = parseInt(match[1] ?? '0', 10);
    const minutes = parseInt(match[2] ?? '0', 10);
    const seconds = parseInt(match[3] ?? '0', 10);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`;
    }

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const iframeAllow = [
    'accelerometer',
    'autoplay',
    'clipboard-write',
    'encrypted-media',
    'gyroscope',
    'picture-in-picture',
    'web-share',
    allowFullScreen ? 'fullscreen' : '',
  ]
    .filter(Boolean)
    .join('; ');

  useEffect(() => {
    setShowPoster(true);
  }, [videoUrl]);

  useEffect(() => {
    const getSchema = async () => {
      if (!videoId || !videoMeta.isFreshpaintVideo) {
        setDuration(null);
        return;
      }

      try {
        const { data } = await axios.get(
          `https://freshpaint-video.com/videos/${videoId}/schema.json`
        );

        setDuration(formatISOTime(data.duration) ?? null);
      } catch (err) {
        console.log(err);
        setDuration(null);
      }
    };

    getSchema();
  }, [videoId, videoMeta.isFreshpaintVideo]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      const eventData =
        typeof event.data === 'string'
          ? (() => {
              try {
                return JSON.parse(event.data);
              } catch {
                return event.data;
              }
            })()
          : event.data;

      if (eventData?.event === 'ended') {
        setShowPoster(true);
      }

      if (
        eventData?.event === 'play' ||
        eventData?.event === 'playing' ||
        eventData?.event === 'started'
      ) {
        setShowPoster(false);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  useEffect(() => {
    const handleWindowBlur = () => {
      window.setTimeout(() => {
        if (document.activeElement === iframeRef.current) {
          setShowPoster(false);
        }
      }, 0);
    };

    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, []);

  if (!videoUrl) {
    return <div className="player-container"></div>;
  }

  return (
    <div className="player-container">
      <iframe
        ref={iframeRef}
        key={iframeSrc}
        src={iframeSrc}
        title={
          videoMeta.isYouTubeVideo
            ? 'YouTube Video Player'
            : 'Freshpaint Video Player'
        }
        allow={iframeAllow}
        allowFullScreen={allowFullScreen}
        onFocus={() => setShowPoster(false)}
      ></iframe>

      {shouldRenderCustomPoster && (
        <div className="video-poster-overlay" aria-hidden="true">
          <div className="gradient-overlay"> </div>

          {hasAuthoredThumbnail ? (
            <div className="thumbnail-img">
              <Image field={Thumbnail} />
            </div>
          ) : (
            shouldUseFreshpaintFallbackThumbnail && (
              <div className="thumbnail-img">
                <img
                  src={fallbackThumbnailUrl}
                  alt=""
                  loading="lazy"
                  className="freshpaint-fallback-thumbnail"
                />
              </div>
            )
          )}

          <div className="play-btn">
            <span className="icon-play-arrow-round">
              <Icons id="icon-play-arrow-round" />
            </span>
          </div>

          {duration && <div className="duration-badge">{duration}</div>}
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;